import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Music } from "lucide-react";
import { format } from "date-fns";
import { apiClient } from "@/App";
import { gigLocalDate } from "@/utils/dateUtils";

function parseGigDate(dateStr) {
  const localDateStr = gigLocalDate(dateStr);
  if (!localDateStr) return null;
  const parts = localDateStr.split("-");
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return null;
}

export default function RSVPPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const [gig, setGig] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(null); // "confirmed" | "declined"

  // Fetch gig details on mount
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await apiClient.get(`/rsvp/${token}`);
        setGig(res.data.gig);
        setInvitation(res.data.invitation);
        // If already responded, show that state. "invited" (sent, awaiting
        // response) must still show the RSVP buttons — only
        // "confirmed"/"declined" are a final answer.
        if (["confirmed", "declined"].includes(res.data.invitation?.status)) {
          setRsvpStatus(res.data.invitation.status);
          setSubmitted(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [token]);

  // Auto-submit if ?response=yes|no came from the email button
  useEffect(() => {
    const autoResponse = searchParams.get("response");
    if (!autoResponse || !gig || submitted || submitting) return;
    if (autoResponse === "yes" || autoResponse === "no") {
      handleRsvp(autoResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gig]);

  const handleRsvp = async (response) => {
    setSubmitting(true);
    try {
      await apiClient.post(`/rsvp/${token}?response=${response}`);
      setRsvpStatus(response === "yes" ? "confirmed" : "declined");
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Error / invalid token ─────────────────────────────────────────────────
  if (error || !gig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Music className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-xl font-semibold mb-2">Invalid invite link</h2>
            <p className="text-muted-foreground text-sm">
              This invite link is invalid or has expired.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gigDate = parseGigDate(gig.date);
  const formattedDate = gigDate ? format(gigDate, "EEEE, MMMM d, yyyy") : "Date TBA";

  // ── Already responded / confirmation ─────────────────────────────────────
  if (submitted) {
    const confirmed = rsvpStatus === "confirmed";
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="h-1 bg-primary w-full fixed top-0 left-0" />
        <div className="max-w-md w-full space-y-6 text-center">
          {/* Icon */}
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto text-2xl"
            style={{ background: confirmed ? "#22c55e22" : "#ef444422" }}
          >
            {confirmed ? "✓" : "✗"}
          </div>

          <div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {confirmed ? "You're in!" : "No worries"}
            </h1>
            <p className="text-zinc-400 text-sm">
              {confirmed
                ? "Thanks! Your response has been recorded. See you at the gig."
                : "Thanks for letting us know. Your response has been recorded."}
            </p>
          </div>

          {/* Gig summary */}
          <Card className="bg-zinc-900 border-zinc-800 text-left">
            <CardContent className="p-4 space-y-2">
              <p className="font-semibold text-white">{gig.artist_name}</p>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                <span>{formattedDate}{gig.start_time ? ` at ${gig.start_time}` : ""}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span>{gig.venue_name}, {gig.city}</span>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-zinc-600">Powered by Fretless</p>
        </div>
      </div>
    );
  }

  // ── RSVP form ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      {/* Brand accent bar */}
      <div className="h-1 bg-primary w-full fixed top-0 left-0" />

      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <p className="font-mono text-xs uppercase tracking-widest text-primary">
            You're invited
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            {gig.artist_name}
          </h1>
        </div>

        {/* Date & time */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "var(--color-orange-soft)" }}>
              <Calendar className="h-5 w-5" style={{ color: "var(--color-orange)" }} />
            </div>
            <div>
              <p className="font-medium text-white">{formattedDate}</p>
              <div className="flex items-center gap-3 text-sm text-zinc-400 mt-0.5">
                {gig.start_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {gig.start_time}
                    {gig.end_time && ` – ${gig.end_time}`}
                  </span>
                )}
                {gig.call_time && (
                  <span className="font-mono text-xs">Call: {gig.call_time}</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Venue */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: "var(--color-orange-soft)" }}>
              <MapPin className="h-5 w-5" style={{ color: "var(--color-orange)" }} />
            </div>
            <div>
              <p className="font-medium text-white">{gig.venue_name}</p>
              {gig.venue_address && (
                <p className="text-sm text-zinc-400 mt-0.5">{gig.venue_address}</p>
              )}
              <p className="text-sm text-zinc-400">{gig.city}</p>
            </div>
          </CardContent>
        </Card>

        {/* RSVP buttons */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button
            onClick={() => handleRsvp("yes")}
            disabled={submitting}
            className="h-14 text-base font-semibold rounded-xl text-white"
            style={{ background: "var(--color-orange)" }}
          >
            {submitting ? (
              <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
            ) : (
              "✓ I'm in"
            )}
          </Button>
          <Button
            onClick={() => handleRsvp("no")}
            disabled={submitting}
            variant="outline"
            className="h-14 text-base font-semibold rounded-xl border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            {submitting ? (
              <span className="h-5 w-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin inline-block" />
            ) : (
              "✗ Can't make it"
            )}
          </Button>
        </div>

        <p className="text-xs text-zinc-600 text-center">Powered by Fretless</p>
      </div>
    </div>
  );
}
